import type { ScoutBadge } from "common/types/user";

/**
 * What the Learning Hub teaches, and where each lesson sits — desktop's copy
 * of native's `features/learning-hub/constants.ts`, the modal in Figma
 * `16013:208570`.
 *
 * Kept in step with native by hand. The structure (tabs, sections, rows) is
 * not in any API; the lessons themselves are CMS albums addressed by name, so
 * both apps read the same artwork and copy.
 */

/** A key of `sync-user`'s `scout_badge.counts`. */
export type LearningCountKey = keyof ScoutBadge["counts"];

/**
 * A lesson's circle — @see `useLearningProgress`. Never stored: "seen" is
 * local, "completed" comes from `scout_badge`.
 */
export type LearningItemState = "not-seen" | "seen" | "completed";

export type LearningHubTabId = "find-places" | "contribute";

export interface LearningItem {
  id: string;
  titleKey: string;
  subtitleKey: string;
  /** The CMS album holding this lesson's steps, by `name`. */
  albumName: string;
  /**
   * The lesson's own heading, where it differs from the row's ("Set up your
   * profile" → "Set up your Sociability profile"). Falls back to the row's.
   */
  lessonTitleKey?: string;
  /** The `scout_badge` task flag that marks the lesson done. Sticky. */
  actionTaskId?: string;
  /** For the two lessons with no task flag: any total above zero is done. Live. */
  actionCountKey?: LearningCountKey;
}

export interface LearningSection {
  id: string;
  headlineKey: string;
  items: LearningItem[];
}

export interface LearningHubTab {
  id: LearningHubTabId;
  labelKey: string;
  sections: LearningSection[];
  /** Set for a tab that is one walkthrough rather than a list of lessons. */
  albumName?: string;
}

export const LEARNING_HUB_TABS: LearningHubTab[] = [
  {
    id: "find-places",
    labelKey: "learningHubTabFindPlaces",
    sections: [],
    albumName: "Find places",
  },
  {
    id: "contribute",
    labelKey: "learningHubTabContribute",
    sections: [
      {
        id: "help-you",
        headlineKey: "learningHubSectionHelpYou",
        items: [
          {
            id: "set-up-profile",
            titleKey: "learningHubProfileTitle",
            subtitleKey: "learningHubProfileSubtitle",
            albumName: "Contribute/Setup profile",
            lessonTitleKey: "learningHubProfileLessonTitle",
            actionTaskId: "accessibility-profile",
          },
          {
            id: "save-places",
            titleKey: "learningHubSavePlacesTitle",
            subtitleKey: "learningHubSavePlacesSubtitle",
            albumName: "Contribute/Save places",
            actionTaskId: "save-venue",
          },
          {
            id: "notifications",
            titleKey: "learningHubNotificationsTitle",
            subtitleKey: "learningHubNotificationsSubtitle",
            albumName: "Contribute/Get updates",
            actionTaskId: "subscribe-venue",
          },
        ],
      },
      {
        id: "help-others",
        headlineKey: "learningHubSectionHelpOthers",
        items: [
          {
            id: "upload-photos",
            titleKey: "learningHubPhotosTitle",
            subtitleKey: "learningHubPhotosSubtitle",
            albumName: "Contribute/Upload photos",
            actionCountKey: "accessibility_photos",
          },
          {
            id: "share-experiences",
            titleKey: "learningHubExperiencesTitle",
            subtitleKey: "learningHubExperiencesSubtitle",
            albumName: "Contribute/Share experience",
            actionTaskId: "add-experience",
          },
          {
            id: "report-issues",
            titleKey: "learningHubReportTitle",
            subtitleKey: "learningHubReportSubtitle",
            albumName: "Contribute/Report alerts",
            actionCountKey: "live_alerts",
          },
        ],
      },
    ],
  },
];

/** Finding places before contributing to them, as native and Figma both open. */
export const DEFAULT_LEARNING_HUB_TAB: LearningHubTabId = "find-places";

export const ALL_LEARNING_ITEMS: LearningItem[] = LEARNING_HUB_TABS.flatMap(
  (tab) => tab.sections.flatMap((section) => section.items),
);

/** What the CMS writes into a field it has nothing for. */
export const CMS_EMPTY_VALUE = "EMPTY";
